import { useCallback, useState } from 'react';

import { useRouter } from 'next/compat/router';

import { APIErrorResponse, useCreateWorkspaceMutation } from '@repo/api-client';
import { fireToast } from '@repo/ui/components/Toast';
import { Button } from '@repo/ui-kit/components/core/Button';
import { Dialog } from '@repo/ui-kit/components/core/Dialog';
import { TextInput } from '@repo/ui-kit/components/core/Input';
import { P3, H4 } from '@repo/ui-kit/components/core/Typography';

import { FlexColumn } from 'styles/layouts';

import { SubmitArea, DialogTitle } from './styles';

export const CreateWorkspaceDialog: React.FC<{
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onCreate: () => void;
  onError?: (error: APIErrorResponse<'post', '/workspaces/create'>) => void;
}> = ({ isOpen, setIsOpen, onCreate, onError }) => {
  const [spaceName, setSpaceName] = useState<string>('');
  const router = useRouter();
  const { mutateAsync, isPending } = useCreateWorkspaceMutation();

  const onClose = useCallback(() => {
    setIsOpen(false);
    setSpaceName('');
  }, [setIsOpen]);

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, [setIsOpen]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    try {
      const response = await mutateAsync({ body: { name: spaceName.trim() } });
      onClose();
      fireToast({ label: 'Team created', error: false });
      onCreate();
      router?.push(`/workspace/${response.data.id}/projects`);
    } catch (e: unknown) {
      const error = e as APIErrorResponse<'post', '/workspaces/create'>;
      if (error.error === 'FREE_WORKSPACE_LIMIT_REACHED') {
        onClose();
      } else {
        fireToast({ label: 'Failed to create team', error: true });
      }
      if (onError) {
        onError(error);
      }
    }
  }, [mutateAsync, spaceName, router, onClose, onCreate, onError]);

  return (
    <Dialog.Root
      modal={true}
      open={isOpen}
      onOpenChange={(open) => (open ? handleOpen() : handleClose())}
    >
      <Dialog.Content height="292px" title="Create team" width="400px">
        <FlexColumn gap="16px">
          <DialogTitle padding="16px 24px">
            <H4 bold>Create team</H4>
          </DialogTitle>
          <FlexColumn gap="16px" padding="0px 24px">
            <FlexColumn gap="16px">
              <P3>
                Organize projects, collaborate with others, and manage all your
                design assets in one place.
              </P3>
              <FlexColumn gap="8px">
                <TextInput
                  disabled={isPending}
                  label="Team name"
                  maxLength={100}
                  placeholder="Enter name"
                  value={spaceName}
                  onChanging={(value) => setSpaceName(value || '')}
                />
                <P3>You can always change the name in the settings.</P3>
              </FlexColumn>
            </FlexColumn>
            <SubmitArea>
              <Button
                disabled={spaceName.trim().length === 0}
                isLoading={isPending}
                name="createTeam"
                onClick={handleSubmit}
              >
                <Button.Label>{isPending ? '' : 'Create'}</Button.Label>
              </Button>
            </SubmitArea>
          </FlexColumn>
        </FlexColumn>
      </Dialog.Content>
    </Dialog.Root>
  );
};
